import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FaArrowRight } from "react-icons/fa";
import api from "../api";

export default function CategoriesSection() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSlide, setActiveSlide] = useState(0);
  const navigate = useNavigate();
  const sliderRef = useRef(null);

  useEffect(() => {
    async function fetchCategories() {
      try {
        const res = await api.get("/api/sections/");
        setCategories(Array.isArray(res.data) ? res.data.filter((cat) => cat.is_active) : []);
      } catch (err) {
        console.error("Error loading collections:", err);
        setCategories([]);
      } finally {
        setLoading(false);
      }
    }
    fetchCategories();
  }, []);

  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;
    const onScroll = () => {
      const cards = slider.querySelectorAll('[data-category-card="true"]');
      if (!cards.length) return;
      let closest = 0;
      let distance = Infinity;
      cards.forEach((card, index) => {
        const d = Math.abs(card.offsetLeft - slider.scrollLeft);
        if (d < distance) { distance = d; closest = index; }
      });
      setActiveSlide(closest);
    };
    slider.addEventListener("scroll", onScroll, { passive: true });
    return () => slider.removeEventListener("scroll", onScroll);
  }, [categories]);

  const goToSlide = (index) => {
    const cards = sliderRef.current?.querySelectorAll('[data-category-card="true"]');
    if (!cards?.[index]) return;
    sliderRef.current.scrollTo({ left: cards[index].offsetLeft, behavior: "smooth" });
    setActiveSlide(index);
  };

  return (
    <section className="relative overflow-hidden bg-[#F8F3E7] py-16 sm:py-20 lg:py-24">
      <div className="absolute -left-24 top-10 h-64 w-64 rounded-full bg-[#0F766E]/10 blur-3xl" />
      <div className="absolute -right-24 bottom-0 h-72 w-72 rounded-full bg-white/80 blur-3xl" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-10 flex flex-col gap-5 sm:mb-12 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#0F766E]">Shop by collection</p>
            <h2 className="home-typewriter mt-3 text-3xl font-bold tracking-tight text-[#173C39] sm:text-4xl lg:text-5xl">Find a rug with a story</h2>
            <p className="mt-4 text-base leading-7 text-slate-600 sm:text-lg">Explore handmade Afghan collections selected for texture, character, and timeless interiors.</p>
          </div>
          <button type="button" onClick={() => navigate("/products/All")} className="hidden items-center gap-2 rounded-full border border-[#173C39]/20 bg-white px-5 py-3 text-sm font-semibold text-[#173C39] shadow-sm transition hover:border-[#0F766E] hover:text-[#0F766E] md:inline-flex">View all rugs <FaArrowRight className="text-xs" /></button>
        </div>
        <div ref={sliderRef} className="flex gap-4 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:gap-5">
          {loading ? [...Array(4)].map((_, i) => <div key={i} className="h-[350px] w-[82vw] flex-none animate-pulse rounded-[2rem] bg-white/70 sm:w-[320px] lg:w-[350px]" />) : categories.map((cat, index) => (
            <motion.article key={cat.id} data-category-card="true" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }} transition={{ duration: 0.45, delay: index * 0.06 }} onClick={() => cat?.name && navigate(`/products/${encodeURIComponent(cat.name)}`)} className="group relative h-[350px] w-[82vw] flex-none cursor-pointer snap-start overflow-hidden rounded-[2rem] bg-[#173C39] shadow-lg ring-1 ring-black/5 sm:w-[320px] lg:w-[350px]">
              {cat.image ? <img src={cat.image} alt={cat.name || "A1 Rugs collection"} className="h-full w-full object-cover transition duration-700 group-hover:scale-105" /> : <div className="h-full w-full bg-[#0F766E]" />}
              <div className="absolute inset-0 bg-gradient-to-t from-[#102b29]/95 via-black/25 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-6 sm:p-7"><div className="mb-3 h-px w-10 bg-[#F8F3E7]/70 transition-all duration-300 group-hover:w-16" /><h3 className="text-2xl font-bold text-white sm:text-3xl">{cat.name}</h3>{cat.description && <p className="mt-2 line-clamp-2 text-sm leading-6 text-white/75">{cat.description}</p>}<span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-white">Explore collection <FaArrowRight className="text-xs transition-transform group-hover:translate-x-1" /></span></div>
            </motion.article>
          ))}
        </div>
        {!loading && categories.length > 1 && <div className="mt-6 flex justify-center gap-2">{categories.map((category, index) => <button key={category.id} type="button" aria-label={`Go to ${category.name}`} onClick={() => goToSlide(index)} className={`h-2 rounded-full transition-all ${activeSlide === index ? "w-8 bg-[#0F766E]" : "w-2 bg-[#0F766E]/25"}`} />)}</div>}
        {!loading && categories.length === 0 && <div className="py-10 text-center text-slate-500">Collections will appear here soon.</div>}
      </div>
    </section>
  );
}
